import React, { useEffect, useState } from 'react';
import { CheckCircle, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'ct_onboarding_survey_done';

/**
 * Onboarding survey ("Što te najviše zanima?") shown after registration.
 * Powers personalised newsletter segmentation.
 */
export default function OnboardingSurvey({ open, email, userId, onClose }) {
  const [options, setOptions] = useState({ interests: [], frequencies: ['daily', 'weekly', 'monthly'] });
  const [picked, setPicked] = useState([]);
  const [freq, setFreq] = useState('weekly');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/onboarding/survey/options`).then(r => r.json()).then(setOptions).catch(() => {});
  }, [open]);

  const toggle = (k) => setPicked(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await fetch(`${API}/api/onboarding/survey`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, user_id: userId, interests: picked, frequency: freq }),
      });
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
      setDone(true);
      setTimeout(() => { onClose && onClose(); }, 1500);
    } catch {}
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="onboarding-survey">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
        {done ? (
          <div className="text-center py-8" data-testid="onboarding-done">
            <CheckCircle size={48} className="mx-auto text-emerald-500 mb-2" />
            <p className="font-bold text-gray-900 dark:text-white">Hvala! Personaliziramo vaš sadržaj.</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-black text-navy dark:text-white">Što te najviše zanima?</h3>
                <p className="text-sm text-gray-500 mt-1">Odaberite teme koje volite — slat ćemo vam samo to što vas zanima.</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="onboarding-close">
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {options.interests.map(opt => (
                <button key={opt.key} onClick={() => toggle(opt.key)}
                  data-testid={`survey-interest-${opt.key}`}
                  className={`px-3 py-2 rounded-xl border text-sm text-left transition-all ${
                    picked.includes(opt.key)
                      ? 'border-terracotta bg-terracotta/10 text-terracotta font-bold'
                      : 'border-gray-200 dark:border-gray-600 hover:border-terracotta/50'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Učestalost newslettera</p>
              <div className="flex gap-2">
                {options.frequencies.map(f => (
                  <button key={f} onClick={() => setFreq(f)} data-testid={`survey-freq-${f}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${freq === f ? 'bg-navy text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                    {f === 'daily' ? 'Dnevno' : f === 'weekly' ? 'Tjedno' : 'Mjesečno'}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={submit} disabled={submitting || picked.length === 0}
              data-testid="onboarding-submit"
              className="w-full py-2.5 rounded-xl bg-terracotta text-white font-bold disabled:opacity-50 hover:bg-terracotta/90 inline-flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              Spremi preferencije
            </button>
          </>
        )}
      </div>
    </div>
  );
}
